/**
 * Rapprochement entre le nom d'un client dans l'app (ex. « SOGARIS CD TRANS »)
 * et le nom comptable d'un client bloqué (ex. « M2 TRANS »).
 *
 * Règle : après nettoyage, TOUS les mots distinctifs du nom le plus court doivent
 * se retrouver dans l'autre nom. Les mots génériques (TRANS, TRANSPORT, EXPRESS,
 * LOG, IDF, SOGARIS, COMPTE FERME…) ne suffisent jamais à eux seuls à faire un match.
 *
 * Ancien bug corrigé : « M2 TRANS » et « TRANS DB » se réduisaient au seul mot
 * « trans », qui faisait matcher tout client contenant TRANS (ex. SOGARIS CD TRANS).
 * À l'inverse « AA TRANSPORT », « KB TRANSPORT » ou « MH TRANSPORT LOGISTIQUE » ne
 * matchaient plus rien (mots de 2 lettres ignorés + préfixe SOGARIS).
 */

// Mots ignorés partout : formes juridiques, mots de liaison, annotations internes.
const NOISE = new Set([
  'sogaris',
  'sarl',
  'sas',
  'sa',
  'eurl',
  'sasu',
  'sci',
  'ei',
  'bv',
  'gmbh',
  'ltd',
  'inc',
  'and',
  'et',
  'le',
  'la',
  'les',
  'de',
  'des',
  'du',
  'en',
  'pour',
  'ex',
  'site',
  'compte',
  'ferme',
  'ne',
  'plus',
  'laver',
  'saisir',
  'client',
  'bloque',
  'impayes',
]);

// Mots génériques du métier : gardés pour départager, mais jamais distinctifs.
const GENERIC = new Set([
  'trans',
  'transport',
  'transports',
  'logistique',
  'logistics',
  'log',
  'express',
  'service',
  'services',
  'group',
  'groupe',
  'location',
  'distribution',
  'fr',
  'france',
  'paris',
  'idf',
  'rungis',
]);

export function normalizeName(s: string): string {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stem(t: string): string {
  // pluriel simple (PRESTATIONS -> PRESTATION) sans casser les sigles (CLS, GTS)
  return t.length >= 5 && t.endsWith('s') ? t.slice(0, -1) : t;
}

/** Tous les mots utiles (hors bruit), mots de 2 lettres compris (M2, CD, DB, AA, KB, MH…). */
function rawTokens(s: string): string[] {
  return normalizeName(s)
    .split(' ')
    .filter((t) => t.length >= 2 && !NOISE.has(t));
}

function allTokens(s: string): string[] {
  return rawTokens(s).map(stem);
}

/** Mots distinctifs uniquement (hors mots génériques du métier, testés AVANT le singulier). */
function distinctiveTokens(s: string): string[] {
  return rawTokens(s)
    .filter((t) => !GENERIC.has(t))
    .map(stem);
}

function isSubset(small: string[], big: string[]): boolean {
  const set = new Set(big);
  return small.every((t) => set.has(t));
}

function subsetMatch(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) {
    return false;
  }
  const [small, big] = a.length <= b.length ? [a, b] : [b, a];
  return isSubset(small, big);
}

function levenshtein(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  const m = a.length;
  const n = b.length;
  if (m === 0) {
    return n;
  }
  if (n === 0) {
    return m;
  }
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) {
    prev[j] = j;
  }
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[n];
}

export function isBlockedMatch(appName: string, blockedName: string): boolean {
  const fullA = allTokens(appName);
  const fullB = allTokens(blockedName);
  if (fullA.length === 0 || fullB.length === 0) {
    return false;
  }
  if (fullA.join(' ') === fullB.join(' ')) {
    return true;
  }

  const distA = distinctiveTokens(appName);
  const distB = distinctiveTokens(blockedName);

  // Cas normal : les mots distinctifs du nom le plus court sont tous dans l'autre.
  if (distA.length > 0 && distB.length > 0) {
    if (subsetMatch(distA, distB)) {
      return true;
    }
  } else if (subsetMatch(fullA, fullB)) {
    // Nom fait uniquement de mots génériques (ex. « TRANS EXPRESS ») :
    // on exige alors que TOUS ses mots soient présents dans l'autre nom.
    return true;
  }

  // Variantes d'orthographe très proches (ex. « TRANSPORT RTH ET FILS » / « TRANSPORTS RTH & FILS »).
  const sa = distA.join(' ');
  const sb = distB.join(' ');
  if (sa.length >= 4 && sb.length >= 4) {
    const ratio = 1 - levenshtein(sa, sb) / Math.max(sa.length, sb.length);
    if (ratio >= 0.85) {
      return true;
    }
  }
  return false;
}

export function findBlockedMatch<T extends { name: string }>(appName: string, list: T[]): T | null {
  if (!appName || list.length === 0) {
    return null;
  }
  for (const c of list) {
    if (isBlockedMatch(appName, c.name)) {
      return c;
    }
  }
  return null;
}
